import {
  HamburgerMenuIcon,
  QuestionMarkCircledIcon,
} from '@radix-ui/react-icons';
import { useEmbeddedAuth } from '@src/contexts/embedded-auth/useEmbeddedAuth';
import { Button } from '@repo/ui/components/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@repo/ui/components/DropdownMenu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/components/Tooltip';
import { cn } from '@repo/ui/utils';
import { APP_NAME } from '@src/config';
import { useGetUser } from '@src/api/useUser';
import { useHasPermission } from '@src/hooks/useHasPermission';
import { useIsMobile } from '@src/hooks/useIsMobile';
import { helpButtonPaths } from '@src/lib/help-button-paths';
import { getFullName } from '@src/lib/utils';
import { useTourStore } from '@src/stores/useTourStore';
import { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MenuContext } from '../../contexts/MenuContext';
import { AddUniversityDialog } from './AddUniversityDialog';
import { NotificationsBadge } from './NotificationsBagde';
import { UniSelector } from './UniSelector';

const TopBar = () => {
  const { isAuthenticated } = useEmbeddedAuth();
  const location = useLocation();
  const { data: user } = useGetUser(isAuthenticated);

  const { menu, setMenu } = useContext(MenuContext);
  const { hasPermission } = useHasPermission();
  const isMobile = useIsMobile();
  const startTour = useTourStore((state) => state.startTour);

  const helpPathMatch = helpButtonPaths.find(({ match }) =>
    match(location.pathname)
  );
  const shouldShowHelpButton = !!helpPathMatch;

  const handleStartTour = () => {
    if (helpPathMatch) {
      startTour(helpPathMatch.tour);
    }
  };

  return (
    <div className={cn('sticky', 'top-0', 'z-10', 'relative')}>
      <div
        id="header-wrapper"
        className={cn(
          'shadow-transparent',
          'border-0',
          'bg-background',
          'text-foreground'
        )}
      >
        <div
          className={cn(
            'header-title-wrapper',
            'sticky',
            'flex',
            'flex-row',
            'items-center',
            'h-full'
          )}
        >
          <div
            className={cn(
              menu ? 'text-primary-foreground' : 'text-primary',
              menu ? 'bg-primary' : 'bg-background',
              menu && 'md:w-[var(--nav-width)]',
              'flex',
              'items-center',
              'self-stretch'
            )}
          >
            <Button
              variant="primary"
              size="icon"
              className={cn('mx-2', 'shrink-0')}
              onClick={() => setMenu(!menu)}
              aria-label={menu ? 'Close navigation' : 'Open navigation'}
              aria-expanded={menu}
              aria-controls="nav-wrapper"
            >
              <HamburgerMenuIcon className="h-5 w-5" />
            </Button>
            <Link
              to="/"
              id="header-title"
              aria-label="Go to home"
              className={cn(
                menu || !hasPermission('manage-group-universities')
                  ? 'flex'
                  : 'hidden sm:flex',
                'items-center',
                'h-full',
                'w-[80px] md:max-w-[183px]',
                menu && 'min-w-[183px]',
                'py-2 pr-2 md:py-4 md:pr-4',
                menu ? 'text-primary-foreground' : 'text-primary',
                menu ? 'bg-primary' : 'bg-background'
              )}
            >
              <h2 className={cn('text-lg', 'font-semibold', 'tracking-tight')}>
                {APP_NAME}
              </h2>
            </Link>
          </div>
          {hasPermission('manage-group-universities') && (
            <UniSelector
              className={cn(
                'ml-[5px] md:ml-[20px]',
                'max-w-[220px] md:max-w-[400px] lg:max-w-[600px]'
              )}
            />
          )}
        </div>
        {hasPermission('create-university') && <AddUniversityDialog />}

        {shouldShowHelpButton && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="dark"
                  size="sm"
                  onClick={handleStartTour}
                  aria-label="Take a tour"
                >
                  <QuestionMarkCircledIcon className="h-5 w-5" />
                  {!isMobile && (
                    <span className={cn('whitespace-nowrap', 'ml-2')}>
                      Take a tour
                    </span>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Interactive Guide</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        <NotificationsBadge />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" aria-label="Profile">
              <svg
                className={cn('h-5', 'w-5', !isMobile && 'mr-2')}
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.5 0.875C5.49797 0.875 3.875 2.49797 3.875 4.5C3.875 6.15288 4.98124 7.54738 6.49373 7.98351C5.2997 8.12901 4.27557 8.55134 3.50407 9.31167C2.52216 10.2794 2.02502 11.72 2.02502 13.5999C2.02502 13.8623 2.23769 14.0749 2.50002 14.0749C2.76236 14.0749 2.97502 13.8623 2.97502 13.5999C2.97502 11.8799 3.42786 10.7206 4.17091 9.9883C4.91536 9.25463 6.02674 8.87499 7.49995 8.87499C8.97317 8.87499 10.0846 9.25463 10.8291 9.98831C11.5721 10.7206 12.025 11.8799 12.025 13.5999C12.025 13.8623 12.2376 14.0749 12.5 14.0749C12.7623 14.075 12.975 13.8623 12.975 13.6C12.975 11.72 12.4778 10.2794 11.4959 9.31166C10.7244 8.55135 9.70025 8.12903 8.50625 7.98352C10.0187 7.5474 11.125 6.15289 11.125 4.5C11.125 2.49797 9.50203 0.875 7.5 0.875ZM4.825 4.5C4.825 3.02264 6.02264 1.825 7.5 1.825C8.97736 1.825 10.175 3.02264 10.175 4.5C10.175 5.97736 8.97736 7.175 7.5 7.175C6.02264 7.175 4.825 5.97736 4.825 4.5Z"
                  fill="currentColor"
                  fillRule="evenodd"
                  clipRule="evenodd"
                />
              </svg>
              {!isMobile && (
                <span className={cn('whitespace-nowrap')}>
                  {getFullName(user)}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className={cn('w-[200px]')}>
            <DropdownMenuLabel>{getFullName(user)}</DropdownMenuLabel>
            <DropdownMenuItem asChild>
              <Link to="/profile">Edit profile</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/sign-out">Sign out</Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default TopBar;
